/**
 * Brand Variant Themes
 * 
 * Pre-built brand personalities for Statsig experimentation
 */

import { Theme } from './base';

/**
 * Bold Theme
 * High contrast, saturated colors, strong presence
 */
export const boldTheme: Theme = {
  name: 'Bold',
  description: 'High contrast and saturated colors for strong visual impact',
  light: {
    background: { h: 0, s: 0, l: 100 },
    foreground: { h: 0, s: 0, l: 0 },
    primary: { h: 340, s: 82, l: 52 }, // Vibrant red-pink
    'primary-foreground': { h: 0, s: 0, l: 100 },
    secondary: { h: 200, s: 98, l: 39 }, // Electric blue
    'secondary-foreground': { h: 0, s: 0, l: 100 },
    accent: { h: 45, s: 100, l: 51 }, // Bold yellow
    'accent-foreground': { h: 0, s: 0, l: 0 },
    muted: { h: 0, s: 0, l: 92 },
    'muted-foreground': { h: 0, s: 0, l: 40 },
    destructive: { h: 0, s: 100, l: 50 },
    'destructive-foreground': { h: 0, s: 0, l: 100 },
    border: { h: 0, s: 0, l: 85 },
    input: { h: 0, s: 0, l: 90 },
    ring: { h: 340, s: 82, l: 52 },
    card: { h: 0, s: 0, l: 98 },
    'card-foreground': { h: 0, s: 0, l: 0 },
    popover: { h: 0, s: 0, l: 100 },
    'popover-foreground': { h: 0, s: 0, l: 0 },
  },
  dark: {
    background: { h: 0, s: 0, l: 5 },
    foreground: { h: 0, s: 0, l: 100 },
    primary: { h: 340, s: 82, l: 60 },
    'primary-foreground': { h: 0, s: 0, l: 100 },
    secondary: { h: 200, s: 98, l: 50 },
    'secondary-foreground': { h: 0, s: 0, l: 0 },
    accent: { h: 45, s: 100, l: 55 },
    'accent-foreground': { h: 0, s: 0, l: 0 },
    muted: { h: 0, s: 0, l: 15 },
    'muted-foreground': { h: 0, s: 0, l: 65 },
    destructive: { h: 0, s: 100, l: 60 },
    'destructive-foreground': { h: 0, s: 0, l: 100 },
    border: { h: 0, s: 0, l: 20 },
    input: { h: 0, s: 0, l: 15 },
    ring: { h: 340, s: 82, l: 60 },
    card: { h: 0, s: 0, l: 8 },
    'card-foreground': { h: 0, s: 0, l: 100 },
    popover: { h: 0, s: 0, l: 8 },
    'popover-foreground': { h: 0, s: 0, l: 100 },
  },
  shadows: {
    intensity: 'strong',
  },
};

/**
 * Minimal Theme
 * Low contrast, muted colors, subtle effects
 */
export const minimalTheme: Theme = {
  name: 'Minimal',
  description: 'Subtle, low contrast design with muted colors',
  light: {
    background: { h: 0, s: 0, l: 99 },
    foreground: { h: 0, s: 0, l: 15 },
    primary: { h: 0, s: 0, l: 25 }, // Nearly black
    'primary-foreground': { h: 0, s: 0, l: 99 },
    secondary: { h: 0, s: 0, l: 94 },
    'secondary-foreground': { h: 0, s: 0, l: 15 },
    accent: { h: 0, s: 0, l: 90 },
    'accent-foreground': { h: 0, s: 0, l: 15 },
    muted: { h: 0, s: 0, l: 96 },
    'muted-foreground': { h: 0, s: 0, l: 55 },
    destructive: { h: 0, s: 40, l: 45 },
    'destructive-foreground': { h: 0, s: 0, l: 99 },
    border: { h: 0, s: 0, l: 93 },
    input: { h: 0, s: 0, l: 96 },
    ring: { h: 0, s: 0, l: 25 },
    card: { h: 0, s: 0, l: 100 },
    'card-foreground': { h: 0, s: 0, l: 15 },
    popover: { h: 0, s: 0, l: 100 },
    'popover-foreground': { h: 0, s: 0, l: 15 },
  },
  dark: {
    background: { h: 0, s: 0, l: 8 },
    foreground: { h: 0, s: 0, l: 90 },
    primary: { h: 0, s: 0, l: 85 },
    'primary-foreground': { h: 0, s: 0, l: 8 },
    secondary: { h: 0, s: 0, l: 15 },
    'secondary-foreground': { h: 0, s: 0, l: 90 },
    accent: { h: 0, s: 0, l: 20 },
    'accent-foreground': { h: 0, s: 0, l: 90 },
    muted: { h: 0, s: 0, l: 12 },
    'muted-foreground': { h: 0, s: 0, l: 60 },
    destructive: { h: 0, s: 40, l: 55 },
    'destructive-foreground': { h: 0, s: 0, l: 90 },
    border: { h: 0, s: 0, l: 18 },
    input: { h: 0, s: 0, l: 12 },
    ring: { h: 0, s: 0, l: 85 },
    card: { h: 0, s: 0, l: 10 },
    'card-foreground': { h: 0, s: 0, l: 90 },
    popover: { h: 0, s: 0, l: 10 },
    'popover-foreground': { h: 0, s: 0, l: 90 },
  },
  shadows: {
    intensity: 'subtle',
  },
};

/**
 * Editorial Theme
 * Magazine-style with serif typography emphasis
 */
export const editorialTheme: Theme = {
  name: 'Editorial',
  description: 'Magazine-inspired design with strong typography',
  light: {
    background: { h: 40, s: 20, l: 98 }, // Warm white
    foreground: { h: 0, s: 0, l: 10 },
    primary: { h: 20, s: 80, l: 45 }, // Rich brown-orange
    'primary-foreground': { h: 40, s: 20, l: 98 },
    secondary: { h: 200, s: 20, l: 50 },
    'secondary-foreground': { h: 40, s: 20, l: 98 },
    accent: { h: 160, s: 50, l: 45 }, // Editorial green
    'accent-foreground': { h: 40, s: 20, l: 98 },
    muted: { h: 40, s: 15, l: 93 },
    'muted-foreground': { h: 0, s: 0, l: 45 },
    destructive: { h: 0, s: 70, l: 50 },
    'destructive-foreground': { h: 40, s: 20, l: 98 },
    border: { h: 40, s: 15, l: 88 },
    input: { h: 40, s: 15, l: 95 },
    ring: { h: 20, s: 80, l: 45 },
    card: { h: 0, s: 0, l: 100 },
    'card-foreground': { h: 0, s: 0, l: 10 },
    popover: { h: 0, s: 0, l: 100 },
    'popover-foreground': { h: 0, s: 0, l: 10 },
  },
  dark: {
    background: { h: 0, s: 0, l: 10 },
    foreground: { h: 40, s: 20, l: 95 },
    primary: { h: 20, s: 70, l: 60 },
    'primary-foreground': { h: 0, s: 0, l: 10 },
    secondary: { h: 200, s: 20, l: 35 },
    'secondary-foreground': { h: 40, s: 20, l: 95 },
    accent: { h: 160, s: 40, l: 55 },
    'accent-foreground': { h: 0, s: 0, l: 10 },
    muted: { h: 0, s: 0, l: 18 },
    'muted-foreground': { h: 40, s: 15, l: 60 },
    destructive: { h: 0, s: 70, l: 60 },
    'destructive-foreground': { h: 40, s: 20, l: 95 },
    border: { h: 0, s: 0, l: 20 },
    input: { h: 0, s: 0, l: 15 },
    ring: { h: 20, s: 70, l: 60 },
    card: { h: 0, s: 0, l: 12 },
    'card-foreground': { h: 40, s: 20, l: 95 },
    popover: { h: 0, s: 0, l: 12 },
    'popover-foreground': { h: 40, s: 20, l: 95 },
  },
  fontFamily: {
    display: "'Playfair Display', serif",
    sans: "'Inter', sans-serif",
    serif: "'Lora', serif",
  },
};

/**
 * Tech Theme
 * Code-inspired, monospace accents, precise
 */
export const techTheme: Theme = {
  name: 'Tech',
  description: 'Developer-focused with monospace details',
  light: {
    background: { h: 210, s: 15, l: 98 },
    foreground: { h: 220, s: 10, l: 15 },
    primary: { h: 180, s: 100, l: 35 }, // Cyan
    'primary-foreground': { h: 0, s: 0, l: 100 },
    secondary: { h: 260, s: 60, l: 55 }, // Purple
    'secondary-foreground': { h: 0, s: 0, l: 100 },
    accent: { h: 140, s: 70, l: 45 }, // Green
    'accent-foreground': { h: 0, s: 0, l: 100 },
    muted: { h: 210, s: 15, l: 92 },
    'muted-foreground': { h: 220, s: 10, l: 50 },
    destructive: { h: 0, s: 80, l: 55 },
    'destructive-foreground': { h: 0, s: 0, l: 100 },
    border: { h: 210, s: 15, l: 88 },
    input: { h: 210, s: 15, l: 95 },
    ring: { h: 180, s: 100, l: 35 },
    card: { h: 0, s: 0, l: 100 },
    'card-foreground': { h: 220, s: 10, l: 15 },
    popover: { h: 0, s: 0, l: 100 },
    'popover-foreground': { h: 220, s: 10, l: 15 },
  },
  dark: {
    background: { h: 220, s: 15, l: 10 },
    foreground: { h: 210, s: 15, l: 90 },
    primary: { h: 180, s: 100, l: 50 },
    'primary-foreground': { h: 220, s: 15, l: 10 },
    secondary: { h: 260, s: 60, l: 60 },
    'secondary-foreground': { h: 220, s: 15, l: 10 },
    accent: { h: 140, s: 70, l: 55 },
    'accent-foreground': { h: 220, s: 15, l: 10 },
    muted: { h: 220, s: 15, l: 18 },
    'muted-foreground': { h: 210, s: 15, l: 65 },
    destructive: { h: 0, s: 80, l: 60 },
    'destructive-foreground': { h: 210, s: 15, l: 90 },
    border: { h: 220, s: 15, l: 20 },
    input: { h: 220, s: 15, l: 15 },
    ring: { h: 180, s: 100, l: 50 },
    card: { h: 220, s: 15, l: 12 },
    'card-foreground': { h: 210, s: 15, l: 90 },
    popover: { h: 220, s: 15, l: 12 },
    'popover-foreground': { h: 210, s: 15, l: 90 },
  },
  fontFamily: {
    mono: "'JetBrains Mono', monospace",
  },
};

/**
 * Export all brand variants
 */
export const brandVariants = {
  bold: boldTheme,
  minimal: minimalTheme,
  editorial: editorialTheme,
  tech: techTheme,
} as const;

export type BrandVariant = keyof typeof brandVariants;
